// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { CookieConsentConfig } from 'vanilla-cookieconsent';

interface ConsentConfigOptions {
  onConsentChange?: (analyticsAccepted: boolean) => void;
}

export function createCookieConsentConfig(
  options?: ConsentConfigOptions
): CookieConsentConfig {
  return {
    onConsent: () => {
      try {
        const CookieConsent = (window as unknown as Record<string, unknown>)['CookieConsent'] as
          | { acceptedCategory?: (cat: string) => boolean }
          | undefined;
        const accepted = CookieConsent?.acceptedCategory?.('analytics') ?? false;
        options?.onConsentChange?.(accepted);
      } catch {
        // Swallow errors
      }
    },

    onChange: () => {
      try {
        const CookieConsent = (window as unknown as Record<string, unknown>)['CookieConsent'] as
          | { acceptedCategory?: (cat: string) => boolean }
          | undefined;
        const accepted = CookieConsent?.acceptedCategory?.('analytics') ?? false;
        options?.onConsentChange?.(accepted);
      } catch {
        // Swallow errors
      }
    },

    guiOptions: {
      consentModal: {
        layout: 'box',
        position: 'bottom right',
        equalWeightButtons: true,
        flipButtons: false,
      },
      preferencesModal: {
        layout: 'box',
        position: 'right',
        equalWeightButtons: true,
        flipButtons: false,
      },
    },

    categories: {
      necessary: {
        enabled: true,
        readOnly: true,
      },
      analytics: {
        enabled: false,
        readOnly: false,
        autoClear: {
          cookies: [
            { name: /^_clarity/ },
            { name: /^ai_/ },
          ],
        },
      },
    },

    language: {
      default: 'en',
      translations: {
        en: {
          consentModal: {
            title: 'We value your privacy',
            description:
              'We use cookies to enhance your browsing experience and analyse our traffic. By clicking "Accept All", you consent to our use of cookies. You can manage your preferences to learn more.',
            acceptAllBtn: 'Accept All',
            acceptNecessaryBtn: 'Reject All',
            showPreferencesBtn: 'Manage Preferences',
          },
          preferencesModal: {
            title: 'Cookie Preferences',
            acceptAllBtn: 'Accept All',
            acceptNecessaryBtn: 'Reject All',
            savePreferencesBtn: 'Save Preferences',
            closeIconLabel: 'Close',
            serviceCounterLabel: 'Service|Services',
            sections: [
              {
                title: 'Cookie Usage',
                description:
                  'We use cookies to ensure the basic functionalities of the website and to enhance your online experience. You can choose for each category to opt-in/out whenever you want.',
              },
              {
                title: 'Strictly Necessary Cookies',
                description:
                  'These cookies are essential for the proper functioning of our website. Without these cookies, the website would not work properly.',
                linkedCategory: 'necessary',
                cookieTable: {
                  headers: {
                    name: 'Name',
                    domain: 'Domain',
                    description: 'Description',
                    expiration: 'Expiration',
                  },
                  body: [
                    {
                      name: 'cc_cookie',
                      domain: location?.hostname || '',
                      description: 'Stores your cookie consent preferences',
                      expiration: '12 months',
                    },
                  ],
                },
              },
              {
                title: 'Analytics Cookies',
                description:
                  'These cookies collect information about how you use the website. All of the data is anonymised and cannot be used to identify you.',
                linkedCategory: 'analytics',
                cookieTable: {
                  headers: {
                    name: 'Name',
                    domain: 'Domain',
                    description: 'Description',
                    expiration: 'Expiration',
                  },
                  body: [
                    {
                      name: 'Plausible Analytics',
                      domain: 'N/A (cookieless)',
                      description:
                        'Plausible is a privacy-friendly analytics service that does not use cookies. No personal data is collected.',
                      expiration: 'N/A',
                    },
                  ],
                },
              },
              {
                title: 'More Information',
                description:
                  'For any questions regarding our cookie policy and your choices, please <a href="mailto:privacy@finertydata.com" class="cc-link">contact us</a>.',
              },
            ],
          },
        },
      },
    },
  };
}
